import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Clock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  doctorIdentitySchema,
  doctorCredentialsSchema,
  doctorEmploymentSchema,
  doctorScheduleUploadsSchema,
  DoctorIdentityFormData,
  DoctorCredentialsFormData,
  DoctorEmploymentFormData,
  DoctorScheduleUploadsFormData,
} from '../lib/validation';
import { StepIndicator } from './StepIndicator';
import { Autocomplete } from './Autocomplete';
import { FileUpload } from './FileUpload';
import { Doctor, DoctorSchedule, DoctorDocument } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import {
  checkEmailUnique,
  checkLicenseUnique,
  getSpecializations,
  getDepartments,
  AVAILABLE_PRIVILEGES,
} from '../lib/doctorValidation';

interface AddDoctorModalProps {
  onClose: () => void;
  onSuccess: (doctor: Doctor) => void;
}

const STEPS = ['Identity', 'Credentials', 'Employment', 'Schedule & Uploads'];

const SCHEDULE_TEMPLATES = [
  {
    name: 'Morning (10–13, Mon–Fri)',
    slots: [1, 2, 3, 4, 5].map(day => ({
      day_of_week: day,
      start_time: '10:00',
      end_time: '13:00',
      max_appointments: 15,
    })),
  },
  {
    name: 'Evening (17–20, Mon/Wed/Fri)',
    slots: [1, 3, 5].map(day => ({
      day_of_week: day,
      start_time: '17:00',
      end_time: '20:00',
      max_appointments: 12,
    })),
  },
  {
    name: 'Full Day (10–17, Mon–Fri)',
    slots: [1, 2, 3, 4, 5].map(day => ({
      day_of_week: day,
      start_time: '10:00',
      end_time: '17:00',
      max_appointments: 30,
    })),
  },
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const AddDoctorModal: React.FC<AddDoctorModalProps> = ({ onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  
  const [identityData, setIdentityData] = useState<DoctorIdentityFormData | null>(null);
  const [credentialsData, setCredentialsData] = useState<DoctorCredentialsFormData | null>(null);
  const [employmentData, setEmploymentData] = useState<DoctorEmploymentFormData | null>(null);
  const [scheduleData, setScheduleData] = useState<DoctorScheduleUploadsFormData | null>(null);
  
  const [specializations, setSpecializations] = useState<string[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [degreeFiles, setDegreeFiles] = useState<File[]>([]);
  const [idFile, setIdFile] = useState<File | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  
  const [emailWarning, setEmailWarning] = useState<string>('');
  const [licenseWarning, setLicenseWarning] = useState<string>('');

  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [specs, depts] = await Promise.all([
        getSpecializations(),
        getDepartments(),
      ]);
      setSpecializations(specs);
      setDepartments(depts);
    } catch (err) {
      console.error('Error loading reference data:', err);
    }
  };

  const getCurrentSchema = () => {
    switch (currentStep) {
      case 0: return doctorIdentitySchema;
      case 1: return doctorCredentialsSchema;
      case 2: return doctorEmploymentSchema;
      case 3: return doctorScheduleUploadsSchema;
      default: return doctorIdentitySchema;
    }
  };

  const form = useForm<any>({
    resolver: zodResolver(getCurrentSchema()),
    mode: 'onChange',
  });

  const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = form;

  useEffect(() => {
    reset();
    
    if (currentStep === 0 && identityData) {
      Object.entries(identityData).forEach(([key, value]) => {
        setValue(key as any, value);
      });
    } else if (currentStep === 1 && credentialsData) {
      Object.entries(credentialsData).forEach(([key, value]) => {
        setValue(key as any, value);
      });
    } else if (currentStep === 2 && employmentData) {
      Object.entries(employmentData).forEach(([key, value]) => {
        setValue(key as any, value);
      });
    } else if (currentStep === 3 && scheduleData) {
      Object.entries(scheduleData).forEach(([key, value]) => {
        setValue(key as any, value);
      });
    }
  }, [currentStep, reset, setValue]);

  const checkEmailDuplicate = async (email: string) => {
    if (!email) return;
    try {
      const isUnique = await checkEmailUnique(email);
      setEmailWarning(isUnique ? '' : 'This email is already registered');
    } catch (err) {
      console.error('Error checking email:', err);
    }
  };

  const checkLicenseDuplicate = async (license: string) => {
    if (!license) return;
    try {
      const isUnique = await checkLicenseUnique(license);
      setLicenseWarning(isUnique ? '' : 'This license number is already registered');
    } catch (err) {
      console.error('Error checking license:', err);
    }
  };

  const handleNext = async (data: any) => {
    if (currentStep === 0) {
      setIdentityData(data);
      setCurrentStep(1);
    } else if (currentStep === 1) {
      setCredentialsData(data);
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setEmploymentData(data);
      setCurrentStep(3);
    } else if (currentStep === 3) {
      setScheduleData(data);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const uploadFile = async (file: File, doctorId: string, docType: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${doctorId}/${docType}_${Date.now()}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('doctor-documents')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    const { data: urlData } = await supabase.storage
      .from('doctor-documents')
      .createSignedUrl(fileName, 31536000);

    if (!urlData) throw new Error('Failed to create signed URL');

    return urlData.signedUrl;
  };

  const handleSubmitDoctor = async (isDraft: boolean) => {
    if (!identityData || !credentialsData || !employmentData) {
      setError('Please complete all required steps');
      return;
    }

    if (!isDraft && scheduleData?.schedule && scheduleData.schedule.length === 0) {
      setError('At least one OPD slot is required for active doctors');
      return;
    }

    if (!licenseFile) {
      setError('License document is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const doctorData = {
        name: identityData.name,
        email: identityData.email,
        phone: identityData.phone,
        gender: identityData.gender,
        date_of_birth: identityData.date_of_birth,
        license_no: credentialsData.license_no,
        license_council: credentialsData.license_council,
        license_expiry: credentialsData.license_expiry,
        degrees: credentialsData.degrees,
        specialization: credentialsData.specialization,
        subspecialties: credentialsData.subspecialties,
        department: employmentData.department,
        designation: employmentData.designation,
        employment_type: employmentData.employment_type,
        joining_date: employmentData.joining_date,
        status: isDraft ? 'draft' : 'active',
        updated_at: new Date().toISOString(),
      };

      const { data: doctor, error: doctorError } = await supabase
        .from('doctors')
        .insert([doctorData])
        .select()
        .single();

      if (doctorError) throw doctorError;

      const licenseUrl = await uploadFile(licenseFile, doctor.id, 'license');
      
      const documents: any[] = [
        {
          doctor_id: doctor.id,
          doc_type: 'license',
          file_url: licenseUrl,
          file_name: licenseFile.name,
          file_size: licenseFile.size,
        },
      ];

      if (signatureFile) {
        const signatureUrl = await uploadFile(signatureFile, doctor.id, 'signature');
        documents.push({
          doctor_id: doctor.id,
          doc_type: 'signature',
          file_url: signatureUrl,
          file_name: signatureFile.name,
          file_size: signatureFile.size,
        });
        
        await supabase
          .from('doctors')
          .update({ signature_url: signatureUrl })
          .eq('id', doctor.id);
      }

      for (const degreeFile of degreeFiles) {
        const degreeUrl = await uploadFile(degreeFile, doctor.id, 'degree');
        documents.push({
          doctor_id: doctor.id,
          doc_type: 'degree',
          file_url: degreeUrl,
          file_name: degreeFile.name,
          file_size: degreeFile.size,
        });
      }

      if (idFile) {
        const idUrl = await uploadFile(idFile, doctor.id, 'id');
        documents.push({
          doctor_id: doctor.id,
          doc_type: 'id',
          file_url: idUrl,
          file_name: idFile.name,
          file_size: idFile.size,
        });
      }

      const { error: docError } = await supabase
        .from('doctor_documents')
        .insert(documents);

      if (docError) throw docError;

      if (scheduleData?.schedule && scheduleData.schedule.length > 0) {
        const scheduleInserts = scheduleData.schedule.map(slot => ({
          doctor_id: doctor.id,
          day_of_week: slot.day_of_week,
          start_time: slot.start_time,
          end_time: slot.end_time,
          max_appointments: slot.max_appointments,
        }));

        const { error: scheduleError } = await supabase
          .from('doctor_schedule')
          .insert(scheduleInserts);

        if (scheduleError) throw scheduleError;
      }

      if (employmentData.privileges && employmentData.privileges.length > 0) {
        const privilegeInserts = employmentData.privileges.map(priv => ({
          doctor_id: doctor.id,
          privilege: priv,
        }));

        const { error: privError } = await supabase
          .from('doctor_privileges')
          .insert(privilegeInserts);

        if (privError) throw privError;
      }

      onSuccess(doctor);
    } catch (err: any) {
      console.error('Error creating doctor:', err);
      setError(err.message || 'Failed to create doctor');
    } finally {
      setIsSubmitting(false);
    }
  };

  const applyTemplate = (templateIndex: number) => {
    const template = SCHEDULE_TEMPLATES[templateIndex];
    setValue('schedule', template.slots);
  };

  const addScheduleSlot = () => {
    const currentSchedule = watch('schedule') || [];
    setValue('schedule', [
      ...currentSchedule,
      { day_of_week: 1, start_time: '10:00', end_time: '13:00', max_appointments: 10 },
    ]);
  };

  const removeScheduleSlot = (index: number) => {
    const currentSchedule = watch('schedule') || [];
    setValue('schedule', currentSchedule.filter((_: any, i: number) => i !== index));
  };

  const getLicenseExpiryWarning = () => {
    if (!credentialsData?.license_expiry) return null;
    
    const expiryDate = new Date(credentialsData.license_expiry);
    const today = new Date();
    const daysUntilExpiry = Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 30 && daysUntilExpiry > 0) {
      return (
        <div className="flex items-center space-x-2 text-yellow-600 bg-yellow-50 p-2 rounded">
          <Clock className="w-4 h-4" />
          <span className="text-sm">License expires in {daysUntilExpiry} days</span>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Add Doctor</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-4">
          <StepIndicator steps={STEPS} currentStep={currentStep} />

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(handleNext)}>
            {currentStep === 0 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('name')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Dr. John Doe"
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600 mt-1">{errors.name.message as string}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('email')}
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="doctor@hospital.com"
                    onBlur={(e) => checkEmailDuplicate(e.target.value)}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-600 mt-1">{errors.email.message as string}</p>
                  )}
                  {emailWarning && (
                    <p className="text-sm text-yellow-600 mt-1">{emailWarning}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('phone')}
                    type="tel"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+1234567890"
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-600 mt-1">{errors.phone.message as string}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender
                    </label>
                    <select
                      {...register('gender')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth
                    </label>
                    <input
                      {...register('date_of_birth')}
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    License Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('license_no')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="LIC123456"
                    onBlur={(e) => checkLicenseDuplicate(e.target.value)}
                  />
                  {errors.license_no && (
                    <p className="text-sm text-red-600 mt-1">{errors.license_no.message as string}</p>
                  )}
                  {licenseWarning && (
                    <p className="text-sm text-yellow-600 mt-1">{licenseWarning}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    License Council <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('license_council')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Medical Council"
                  />
                  {errors.license_council && (
                    <p className="text-sm text-red-600 mt-1">{errors.license_council.message as string}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    License Expiry <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('license_expiry')}
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.license_expiry && (
                    <p className="text-sm text-red-600 mt-1">{errors.license_expiry.message as string}</p>
                  )}
                  {getLicenseExpiryWarning()}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Degrees <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={(watch('degrees') as string[] || []).join(', ')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="MBBS, MD (comma separated)"
                    onChange={(e) => {
                      const degrees = e.target.value.split(',').map(d => d.trim()).filter(d => d);
                      setValue('degrees', degrees, { shouldValidate: true });
                    }}
                  />
                  {errors.degrees && (
                    <p className="text-sm text-red-600 mt-1">{errors.degrees.message as string}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Specialization <span className="text-red-500">*</span>
                  </label>
                  <Autocomplete
                    value={watch('specialization') || ''}
                    onChange={(value) => setValue('specialization', value)}
                    options={specializations}
                    placeholder="Select or type specialization"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    error={errors.specialization?.message as string}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subspecialties
                  </label>
                  <input
                    type="text"
                    value={(watch('subspecialties') as string[] || []).join(', ')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Interventional, Pediatric (comma separated)"
                    onChange={(e) => {
                      const subs = e.target.value.split(',').map(d => d.trim()).filter(d => d);
                      setValue('subspecialties', subs, { shouldValidate: true });
                    }}
                  />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <Autocomplete
                    value={watch('department') || ''}
                    onChange={(value) => setValue('department', value)}
                    options={departments}
                    placeholder="Select or type department"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    error={errors.department?.message as string}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Designation
                  </label>
                  <input
                    {...register('designation')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Senior Consultant"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employment Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('employment_type')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Type</option>
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="visiting">Visiting</option>
                    <option value="locum">Locum</option>
                  </select>
                  {errors.employment_type && (
                    <p className="text-sm text-red-600 mt-1">{errors.employment_type.message as string}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Joining Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('joining_date')}
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.joining_date && (
                    <p className="text-sm text-red-600 mt-1">{errors.joining_date.message as string}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clinical Privileges
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_PRIVILEGES.map((priv) => (
                      <label key={priv} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          value={priv}
                          onChange={(e) => {
                            const currentPrivileges = watch('privileges') || [];
                            if (e.target.checked) {
                              setValue('privileges', [...currentPrivileges, priv]);
                            } else {
                              setValue('privileges', currentPrivileges.filter((p: string) => p !== priv));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 capitalize">
                          {priv.replace(/_/g, ' ')}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      OPD Schedule
                    </label>
                    <div className="flex space-x-2">
                      {SCHEDULE_TEMPLATES.map((template, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => applyTemplate(idx)}
                          className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                        >
                          {template.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {(watch('schedule') || []).map((slot: any, index: number) => (
                      <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                        <select
                          value={slot.day_of_week}
                          onChange={(e) => {
                            const schedule = watch('schedule') || [];
                            schedule[index].day_of_week = parseInt(e.target.value);
                            setValue('schedule', [...schedule]);
                          }}
                          className="px-2 py-1 border border-gray-300 rounded"
                        >
                          {DAY_NAMES.map((day, idx) => (
                            <option key={idx} value={idx}>{day}</option>
                          ))}
                        </select>
                        <input
                          type="time"
                          value={slot.start_time}
                          onChange={(e) => {
                            const schedule = watch('schedule') || [];
                            schedule[index].start_time = e.target.value;
                            setValue('schedule', [...schedule]);
                          }}
                          className="px-2 py-1 border border-gray-300 rounded"
                        />
                        <span>to</span>
                        <input
                          type="time"
                          value={slot.end_time}
                          onChange={(e) => {
                            const schedule = watch('schedule') || [];
                            schedule[index].end_time = e.target.value;
                            setValue('schedule', [...schedule]);
                          }}
                          className="px-2 py-1 border border-gray-300 rounded"
                        />
                        <input
                          type="number"
                          value={slot.max_appointments}
                          onChange={(e) => {
                            const schedule = watch('schedule') || [];
                            schedule[index].max_appointments = parseInt(e.target.value);
                            setValue('schedule', [...schedule]);
                          }}
                          min="1"
                          className="w-20 px-2 py-1 border border-gray-300 rounded"
                          placeholder="Max"
                        />
                        <button
                          type="button"
                          onClick={() => removeScheduleSlot(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addScheduleSlot}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add Time Slot
                  </button>
                </div>

                <div className="border-t pt-4 space-y-4">
                  <h3 className="font-medium text-gray-900">Documents</h3>
                  
                  <FileUpload
                    label="License Document"
                    required
                    accept=".pdf,.jpg,.jpeg,.png"
                    maxSize={5}
                    onFileSelect={(file) => setLicenseFile(file)}
                    currentFile={licenseFile}
                    onClear={() => setLicenseFile(null)}
                  />

                  <FileUpload
                    label="Signature"
                    accept=".jpg,.jpeg,.png"
                    maxSize={2}
                    onFileSelect={(file) => setSignatureFile(file)}
                    currentFile={signatureFile}
                    onClear={() => setSignatureFile(null)}
                  />

                  <FileUpload
                    label="ID Document"
                    accept=".pdf,.jpg,.jpeg,.png"
                    maxSize={5}
                    onFileSelect={(file) => setIdFile(file)}
                    currentFile={idFile}
                    onClear={() => setIdFile(null)}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Degree Documents
                    </label>
                    {degreeFiles.length > 0 && (
                      <div className="space-y-2 mb-2">
                        {degreeFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => setDegreeFiles(degreeFiles.filter((_, i) => i !== idx))}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <FileUpload
                      label="Add Degree"
                      accept=".pdf,.jpg,.jpeg,.png"
                      maxSize={5}
                      onFileSelect={(file) => setDegreeFiles([...degreeFiles, file])}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center justify-between">
              <div>
                {currentStep > 0 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                    disabled={isSubmitting}
                  >
                    Back
                  </button>
                )}
              </div>

              <div className="flex space-x-3">
                {currentStep === 3 && (
                  <button
                    type="button"
                    onClick={() => handleSubmitDoctor(true)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={isSubmitting}
                  >
                    Save as Draft
                  </button>
                )}
                
                {currentStep < 3 ? (
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    disabled={isSubmitting}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSubmitDoctor(false)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit & Activate'}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
